/**
 * /try Input Widget Wiring (DOM listeners only).
 *
 * WHAT THIS FILE DOES:
 * - Wires the /try textarea, file upload + drag-and-drop, language dropdown,
 *   sign-in button + auth modal, and header auth-state listener.
 * - Owns file attach/remove UI updates and status/subscribe status rendering.
 *
 * WHY THIS EXISTS:
 * - Extracted verbatim from src/client/try-ui.ts so the controller stays
 *   under the god-module line. No behavior changes; identical copy strings.
 *
 * DESIGN NOTE (test contract):
 * - Every helper takes the TryUIController instance and calls back through
 *   its members (`host.showStatus(...)`, `host.handleSubmit()`, ...) so unit
 *   tests that stub instance members keep working unchanged.
 *
 * CALLED BY:
 * - src/client/try-ui.ts (TryUIController.mount() and thin delegates)
 *
 * @file src/client/try-input-widgets.ts
 */

import type { TryUIController } from './try-ui.js';
import type { AuthSnapshot, TryFileUploadProgress } from './try-shared.js';
import { TryMediaIngestLimits, resolveTryErrorMessage } from './try-shared.js';
import { resolveMediaContentType, resolveMediaDurationMs } from './try-utils.js';
import { isDoclingDocumentFile, TryDocumentFileSupport } from '../lib/try-document-file.js';
import { createTryFormatIconElement, type TryFormatIconKind } from './try-format-icons.js';
import { TryModes } from './try-mode-tabs.js';
import { detectTryModeFromTextInput } from './try-input-mode-detection.js';
import { updateLandingMembershipChrome } from './landing-membership-chrome.js';
import { GistPipelineStatusLabels } from '@gist-protocol/core/shared/gist-pipeline-status.js';
import { showPipelineStatus } from './try-submit-flow.js';

const TextareaSizing = Object.freeze({
  /**
   * Max height for the /try textarea before we stop growing it.
   */
  MAX_HEIGHT_PX: 300
});

/**
 * Sets up textarea auto-resize and keyboard shortcuts.
 */
export function setupTextarea(host: TryUIController): void {
  if (!host.textarea) return;

  // Auto-resize textarea as user types
  host.textarea.addEventListener('input', () => {
    host.handleTextareaInput();
  });

  // A-049: Single keydown handler for locked-state guard + Enter submit.
  host.textarea.addEventListener('keydown', (event) => {
    // Check locked state first (speculation blocking).
    const blocked = host.speculationController?.handleTextareaLockedKeydown(event) ?? false;
    if (blocked) {
      event.preventDefault();
      return;
    }

    // Enter to submit (Shift+Enter for newline).
    // Skip during IME composition: CJK/Japanese/Korean users press Enter
    // to commit a candidate, not to submit. `isComposing` covers modern
    // browsers; `keyCode === 229` covers older ones.
    if (
      event.key === 'Enter'
      && !event.shiftKey
      && !event.isComposing
      && event.keyCode !== 229
    ) {
      event.preventDefault();
      void host.handleSubmit();
    }
  });

  host.textarea.addEventListener('paste', (event) => {
    handleTextareaLockedPaste(host, event);
  });
}

/**
 * Sets up file upload via button and drag-and-drop.
 *
 * The drop scope is the create-module wrapper when the tabs flag is on,
 * so dragging anywhere inside the tabbed surface auto-switches to File mode
 * and stages the file. When the flag is off, the legacy input container
 * stays the drop target — flag-off behavior is unchanged.
 */
export function setupFileUpload(host: TryUIController): void {
  if (!host.uploadBtn || !host.fileInput || !host.inputContainer) return;

  // Click to open file picker
  host.uploadBtn.addEventListener('click', () => {
    if (host.speculationController?.isSpeculationBlockingFileInput()) {
      host.speculationController?.notifyInputConflict();
      return;
    }
    host.fileInput?.click();
  });

  // Handle file selection
  host.fileInput.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    if (target.files?.[0]) {
      if (host.modeChanger && host.createModule) {
        host.modeChanger(TryModes.FILE);
      }
      void host.handleFileAttachmentAttempt(target.files[0]);
    }
  });

  const dropScope: HTMLElement = host.createModule ?? host.inputContainer;

  // A-032: Counter-based drag tracking prevents flicker when dragging over child elements.
  dropScope.addEventListener('dragover', (e) => {
    e.preventDefault();
  });

  dropScope.addEventListener('dragenter', (e) => {
    e.preventDefault();
    host.dragOverCounter++;
    if (host.dragOverCounter > 0) {
      setDropHighlight(host, true);
    }
  });

  dropScope.addEventListener('dragleave', (e) => {
    e.preventDefault();
    host.dragOverCounter--;
    if (host.dragOverCounter <= 0) {
      host.dragOverCounter = 0;
      setDropHighlight(host, false);
    }
  });

  dropScope.addEventListener('drop', (e) => {
    e.preventDefault();
    host.dragOverCounter = 0;
    setDropHighlight(host, false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    // Drop forces File mode when the tabbed wrapper is the drop scope so
    // the user's preset matches what's about to happen.
    if (host.modeChanger && host.createModule) {
      host.modeChanger(TryModes.FILE);
    }
    void host.handleFileAttachmentAttempt(file);
  });

  // Remove file button
  host.fileRemove?.addEventListener('click', () => {
    removeFile(host);
  });
}

/**
 * Toggles the drop-target highlight on the active drop scope.
 *
 * - When the create-module wrapper exists (tabs flag on), uses the
 *   `data-drag-active` attribute the plan calls out.
 * - Otherwise toggles the legacy `drag-over` class on the input container
 *   so flag-off styling is byte-compatible.
 */
export function setDropHighlight(host: TryUIController, active: boolean): void {
  if (host.createModule) {
    if (active) {
      host.createModule.setAttribute('data-drag-active', 'true');
    } else {
      host.createModule.removeAttribute('data-drag-active');
    }
    return;
  }
  if (active) {
    host.inputContainer?.classList.add('drag-over');
  } else {
    host.inputContainer?.classList.remove('drag-over');
  }
}

/**
 * Attaches a file and updates the UI.
 */
export function attachFile(host: TryUIController, file: File, durationMs: number | null = null): void {
  host.currentFile = file;
  host.currentFileDurationMs = durationMs;
  // A-009: Clear textarea when a file is attached so only one input mode is active.
  if (host.textarea) {
    host.textarea.value = '';
  }
  if (host.fileName) {
    // A-038: Show file name with formatted size.
    const sizeLabel = file.size >= 1024 * 1024
      ? (file.size / (1024 * 1024)).toFixed(1) + ' MB'
      : Math.round(file.size / 1024) + ' KB';
    host.fileName.textContent = `${file.name} (${sizeLabel})`;
  }
  updateFilePreviewIcon(host, resolveMediaContentType(file) ? 'media' : 'file');
  host.filePreview?.classList.add('visible');
  host.speculationController?.startFileSpeculation(file, { durationMs });
}

/**
 * Swaps the file-preview glyph between document and media artwork.
 */
export function updateFilePreviewIcon(host: TryUIController, kind: TryFormatIconKind): void {
  if (!host.fileIconHost) {
    return;
  }
  host.fileIconHost.replaceChildren(createTryFormatIconElement(kind, 'try-file-icon'));
}

/**
 * Removes the attached file and resets UI.
 */
export function removeFile(host: TryUIController): void {
  host.currentFile = null;
  host.currentFileDurationMs = null;
  if (host.fileInput) {
    host.fileInput.value = '';
  }
  host.filePreview?.classList.remove('visible');
  host.speculationController?.handleFileRemoved();
}

/**
 * Handles textarea input with speculation-aware guards + URL detection.
 */
export function handleTextareaInput(host: TryUIController): void {
  if (!host.textarea) return;

  const blocked = host.speculationController?.handleTextareaInput(
    host.textarea.value,
    Boolean(host.currentFile)
  ) ?? false;
  if (blocked) {
    return;
  }

  syncCreateModeForTextInput(host, host.textarea.value);
  host.updateTextareaHeight();
}

/**
 * Blocks paste when speculation locks input.
 */
export function handleTextareaLockedPaste(host: TryUIController, event: ClipboardEvent): void {
  const blocked = host.speculationController?.handleTextareaLockedPaste() ?? false;
  if (blocked) {
    event.preventDefault();
  }
}

/**
 * Handles file attachment attempts, respecting speculation locks.
 */
export async function handleFileAttachmentAttempt(host: TryUIController, file: File): Promise<void> {
  if (host.speculationController?.handleFileAttachmentAttempt()) {
    // If the file picker selected a file while we are locked, clear the input
    // so the user can retry after the lock clears.
    if (host.fileInput) {
      host.fileInput.value = '';
    }
    return;
  }

  // A-005: Client-side file size gate — reject before the type/duration checks
  // so users don't wait through a long upload only to get a server rejection.
  if (file.size > TryMediaIngestLimits.MAX_FILE_SIZE_BYTES) {
    host.showStatus('File is too large. Maximum size is 1.5 GB.', 'error');
    // A-010: Reset file input so re-selecting the same file fires change.
    if (host.fileInput) host.fileInput.value = '';
    return;
  }

  const isDocument = isDoclingDocumentFile(file);
  const mediaType = resolveMediaContentType(file);
  if (!isDocument && !mediaType) {
    host.showStatus('Unsupported file type. Upload a document (PDF, DOCX, etc.), audio, or video file.', 'error');
    // A-010: Reset file input so re-selecting the same file fires change.
    if (host.fileInput) host.fileInput.value = '';
    return;
  }

  // Document size cap (30 MiB) — matches Docling's upload limit.
  if (isDocument && file.size > TryDocumentFileSupport.MAX_FILE_BYTES) {
    host.showStatus('Document is too large. Maximum size is 30 MB.', 'error');
    // A-010: Reset file input so re-selecting the same file fires change.
    if (host.fileInput) host.fileInput.value = '';
    return;
  }

  if (mediaType) {
    const durationMs = await resolveMediaDurationMs(file);
    if (durationMs !== null && durationMs > TryMediaIngestLimits.MAX_MEDIA_DURATION_MS) {
      host.showStatus('File is too long. Audio/video uploads must be 9.5 hours or less.', 'error');
      // A-010: Reset file input so re-selecting the same file fires change.
      if (host.fileInput) host.fileInput.value = '';
      return;
    }

    attachFile(host, file, durationMs);
    return;
  }

  attachFile(host, file, null);
}

/**
 * Sets up the language selector dropdown.
 */
export function setupLanguageSelector(host: TryUIController): void {
  if (!host.langTrigger || !host.langSelector || !host.langDropdown) return;

  // Toggle dropdown on trigger click
  host.langTrigger.addEventListener('click', () => {
    const isOpen = host.langSelector?.classList.toggle('open');
    host.langTrigger?.setAttribute('aria-expanded', String(isOpen));

    // A-034: Viewport-aware flip — open downward when insufficient space above.
    if (isOpen && host.langTrigger && host.langDropdown) {
      const triggerRect = host.langTrigger.getBoundingClientRect();
      const dropdownHeight = host.langDropdown.scrollHeight;
      const spaceAbove = triggerRect.top;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const shouldFlipDown = spaceAbove < dropdownHeight && spaceBelow > spaceAbove;
      host.langDropdown.classList.toggle('flip-down', shouldFlipDown);
    }
  });

  // Handle language option selection
  const options = host.langDropdown.querySelectorAll('.try-lang-option');
  options.forEach((option) => {
    option.addEventListener('click', () => {
      // Remove selected class from all options
      options.forEach((o) => o.classList.remove('selected'));
      // Add selected class to clicked option
      option.classList.add('selected');

      // Update selected language
      const value = (option as HTMLElement).dataset.value;
      const label = (option as HTMLElement).dataset.label;
      if (value) {
        host.selectedLanguage = value;
      }

      // Update label visibility (only show if not default)
      if (host.langLabel && label) {
        host.langLabel.textContent = label;
        host.langLabel.classList.toggle('visible', host.selectedLanguage !== host.defaultLanguage);
      }

      // Close dropdown
      host.langSelector?.classList.remove('open');
      host.langTrigger?.setAttribute('aria-expanded', 'false');
    });
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!host.langSelector?.contains(e.target as Node)) {
      host.langSelector?.classList.remove('open');
      host.langTrigger?.setAttribute('aria-expanded', 'false');
    }
  });

  // Close dropdown on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && host.langSelector?.classList.contains('open')) {
      host.langSelector.classList.remove('open');
      host.langTrigger?.setAttribute('aria-expanded', 'false');
      host.langTrigger?.focus();
    }
  });

  // A-031: Arrow key, Home/End navigation within the open dropdown.
  host.langDropdown.addEventListener('keydown', (e) => {
    if (!host.langSelector?.classList.contains('open')) return;
    const optionEls = Array.from(
      host.langDropdown?.querySelectorAll<HTMLElement>('.try-lang-option') ?? []
    );
    if (optionEls.length === 0) return;
    const currentIndex = optionEls.indexOf(document.activeElement as HTMLElement);

    let nextIndex: number | null = null;
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < optionEls.length - 1 ? currentIndex + 1 : 0;
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : optionEls.length - 1;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = optionEls.length - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      optionEls[nextIndex].focus();
    }
  });
}

/**
 * Sets up the submit button click handler.
 */
export function setupSubmit(host: TryUIController): void {
  host.submitBtn?.addEventListener('click', () => {
    void host.handleSubmit();
  });
}

/**
 * Sets up the sign-in button and auth modal handlers.
 */
export function setupSignIn(host: TryUIController): void {
  host.signinBtn?.addEventListener('click', () => {
    if (host.isSignedIn) {
      void handleSignOut(host);
      return;
    }
    openAuthModal(host);
  });

  host.authClose?.addEventListener('click', () => {
    closeAuthModal(host);
  });

  host.authOverlay?.addEventListener('click', (event) => {
    if (event.target === host.authOverlay) {
      closeAuthModal(host);
    }
  });

  host.authForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    void handleEmailSignIn(host);
  });

  host.authGoogle?.addEventListener('click', () => {
    void handleGoogleSignIn(host);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && host.authOverlay?.classList.contains('visible')) {
      closeAuthModal(host);
    }
  });

  // A-030: Tab focus trap — cycle focus within the auth modal while it's open.
  host.authModal?.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !host.authOverlay?.classList.contains('visible')) return;
    const focusable = host.authModal?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/**
 * Opens the auth modal and focuses the first input.
 *
 * A-030: Saves the previously focused element and installs a Tab-key
 * focus trap so keyboard users cycle within the modal.
 */
export function openAuthModal(host: TryUIController): void {
  if (!host.authOverlay || !host.authModal) return;
  host.previouslyFocusedElement = document.activeElement as HTMLElement | null;
  host.authOverlay.classList.add('visible');
  host.authOverlay.setAttribute('aria-hidden', 'false');
  clearAuthError(host);
  setTimeout(() => {
    host.authEmail?.focus();
  }, 0);
}

/**
 * Closes the auth modal and clears sensitive inputs.
 *
 * A-030: Restores focus to the element that was active before the modal opened.
 */
export function closeAuthModal(host: TryUIController): void {
  if (!host.authOverlay || !host.authModal) return;
  host.authOverlay.classList.remove('visible');
  host.authOverlay.setAttribute('aria-hidden', 'true');
  if (host.authPassword) {
    host.authPassword.value = '';
  }
  clearAuthError(host);
  if (host.previouslyFocusedElement) {
    host.previouslyFocusedElement.focus();
    host.previouslyFocusedElement = null;
  }
}

/**
 * Handles email/password sign-in flow via Firebase.
 */
export async function handleEmailSignIn(host: TryUIController): Promise<void> {
  if (!host.authEmail || !host.authPassword) return;

  const email = host.authEmail.value.trim();
  const password = host.authPassword.value;

  if (!email || !password) {
    setAuthError(host, 'Please enter your email and password.');
    return;
  }

  setAuthLoading(host, true);
  try {
    await host.client.signInWithEmail(email, password);
    host.showStatus('Logged in successfully.', 'success');
    closeAuthModal(host);
  } catch (error) {
    const message = resolveTryErrorMessage(error);
    setAuthError(host, message);
  } finally {
    setAuthLoading(host, false);
  }
}

/**
 * Handles Google sign-in flow via Firebase popup.
 */
export async function handleGoogleSignIn(host: TryUIController): Promise<void> {
  setAuthLoading(host, true);
  try {
    await host.client.signInWithGoogle();
    host.showStatus('Logged in successfully.', 'success');
    closeAuthModal(host);
  } catch (error) {
    const message = resolveTryErrorMessage(error);
    setAuthError(host, message);
  } finally {
    setAuthLoading(host, false);
  }
}

/**
 * Handles sign-out from web + extension sessions.
 */
export async function handleSignOut(host: TryUIController): Promise<void> {
  host.showStatus('Signing out...', 'loading');
  try {
    await host.client.logout();
    host.showStatus('Signed out.', 'success');
  } catch (error) {
    const message = resolveTryErrorMessage(error);
    host.showStatus(message, 'error');
  }
}

/**
 * Keeps textarea growth logic in one place for normal typing.
 */
export function updateTextareaHeight(host: TryUIController): void {
  if (!host.textarea) {
    return;
  }

  // A-041: Measure scrollHeight first; only reset + update if it changed.
  // Setting height='auto' triggers a layout recalc that causes visible flicker,
  // so we skip it entirely when the content height hasn't changed.
  host.textarea.style.height = 'auto';
  const newScrollHeight = host.textarea.scrollHeight;
  const clampedHeight = Math.min(newScrollHeight, TextareaSizing.MAX_HEIGHT_PX);
  if (clampedHeight !== host.lastScrollHeight) {
    host.lastScrollHeight = clampedHeight;
  }
  host.textarea.style.height = clampedHeight + 'px';
}

/**
 * Renders a "membership required" message followed by a clickable
 * subscribe link inside the /try status area.
 *
 * Why this exists:
 * - When web ingest returns HTTP 402 for signed-in unpaid users, the /try
 *   UI must surface the canonical /join subscribe path (not a dead-end).
 * - Lives here (not /try-shared) because the message includes a real DOM
 *   `<a>`, which can't round-trip through the plain-string
 *   resolveTryErrorMessage() helper.
 *
 * @param message - User-facing status text from resolveTryErrorMessage().
 * @param checkoutUrl - Subscribe URL returned by the backend's 402 body
 *   (falls back to same-origin /join?source=try when the body is missing).
 */
export function showSubscribeStatus(host: TryUIController, message: string, checkoutUrl: string): void {
  if (!host.status) return;

  if (host.successStatusTimerId !== null) {
    clearTimeout(host.successStatusTimerId);
    host.successStatusTimerId = null;
  }

  while (host.status.firstChild) {
    host.status.removeChild(host.status.firstChild);
  }

  host.status.appendChild(document.createTextNode(`${message} `));

  const link = document.createElement('a');
  link.href = checkoutUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'Become a member →';
  link.style.color = '#FF6B6B';
  link.style.fontWeight = '600';
  link.style.textDecoration = 'underline';

  host.status.appendChild(link);
  host.status.className = 'try-status visible error';
}

/**
 * Maps upload progress events to a consistent loading status string.
 */
export function showUploadProgressStatus(host: TryUIController, progress: TryFileUploadProgress): void {
  const percent = progress.percent;
  if (typeof percent === 'number') {
    showPipelineStatus(host, 'processing', `${GistPipelineStatusLabels.LOADING} · ${percent}%`);
    return;
  }
  showPipelineStatus(host, 'processing', GistPipelineStatusLabels.LOADING);
}

/**
 * Keeps the create-module tab aligned with the user's pasted/typed input.
 */
export function syncCreateModeForTextInput(host: TryUIController, value: string): void {
  if (!host.modeChanger || !host.createModule || host.currentFile) {
    return;
  }
  const mode = detectTryModeFromTextInput(value);
  if (!mode) {
    return;
  }
  host.modeChanger(mode);
}

/**
 * Sets the submit button loading state using safe DOM manipulation.
 */
export function setSubmitLoading(host: TryUIController, loading: boolean): void {
  if (!host.submitBtn) return;
  host.submitBtn.disabled = loading;

  // Clear existing content
  while (host.submitBtn.firstChild) {
    host.submitBtn.removeChild(host.submitBtn.firstChild);
  }

  if (loading) {
    // Create spinner SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    // A-042: Use CSS class instead of inline animation so prefers-reduced-motion is respected.
    svg.setAttribute('class', 'try-submit-icon try-spinner');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    circle.setAttribute('stroke-opacity', '0.25');
    svg.appendChild(circle);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M12 2a10 10 0 0 1 10 10');
    path.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path);

    host.submitBtn.appendChild(svg);
    host.submitBtn.appendChild(document.createTextNode(` ${GistPipelineStatusLabels.LOADING}`));
  } else {
    // Create bolt SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'try-submit-icon');
    svg.setAttribute('viewBox', '62 25 76 150');
    svg.setAttribute('aria-hidden', 'true');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M110 35L70 100h25l-5 65 40-75h-25z');
    path.setAttribute('fill', 'currentColor');
    svg.appendChild(path);

    host.submitBtn.appendChild(svg);
    host.submitBtn.appendChild(document.createTextNode(' Get the Gist'));
  }
}

/**
 * Shows a status message.
 *
 * A-035: Success messages auto-clear after 4 seconds.
 */
export function showStatus(host: TryUIController, message: string, type: 'loading' | 'success' | 'error' | 'hidden'): void {
  if (!host.status) return;

  // A-035: Clear any pending auto-hide timer.
  if (host.successStatusTimerId !== null) {
    clearTimeout(host.successStatusTimerId);
    host.successStatusTimerId = null;
  }

  if (type === 'hidden') {
    host.status.textContent = '';
    host.status.className = 'try-status';
    return;
  }
  host.status.textContent = message;
  host.status.className = `try-status visible ${type}`;

  // A-035: Auto-clear success status after 4 seconds.
  if (type === 'success') {
    host.successStatusTimerId = setTimeout(() => {
      host.showStatus('', 'hidden');
    }, 4_000);
  }
}

/**
 * Wires auth state changes into the header UI.
 */
export function setupAuthStateListener(host: TryUIController): void {
  host.client.onAuthStateChange((snapshot) => {
    updateAuthStatus(host, snapshot);
  });
}

/**
 * Updates header UI for signed-in state.
 */
export function updateAuthStatus(host: TryUIController, snapshot: AuthSnapshot): void {
  const authKey = buildAuthKey(snapshot);
  if (host.lastAuthKey && host.lastAuthKey !== authKey) {
    host.speculationController?.resetForAuthChange();
    // If a file is currently attached, re-start speculation under the new auth context.
    // UploadIds are user-scoped, so we cannot safely reuse prior staged uploads.
    if (host.currentFile) {
      host.speculationController?.startFileSpeculation(host.currentFile, {
        durationMs: host.currentFileDurationMs
      });
    } else if (host.textarea) {
      // URL speculation is safe/idempotent, but resetting means we should re-arm it.
      host.speculationController?.handleTextareaInput(host.textarea.value, false);
    }
  }
  host.lastAuthKey = authKey;

  const isAuthenticated = snapshot.isAuthenticated;

  host.isSignedIn = isAuthenticated;

  if (host.signinBtn) {
    host.signinBtn.textContent = isAuthenticated ? 'Sign Out' : 'Login';
  }
  updateLandingMembershipChrome({
    isAuthenticated,
    isPaid: snapshot.paidEntitlement === true
  });
}

/**
 * Builds a stable auth fingerprint for speculation resets.
 */
export function buildAuthKey(snapshot: AuthSnapshot): string {
  const userKey = snapshot.user?.uid ?? snapshot.user?.email ?? 'anonymous';
  const workerKey = snapshot.workerToken ? 'worker' : 'none';
  const sessionKey = snapshot.isAuthenticated ? 'authenticated' : 'anonymous';
  return `${snapshot.mode}:${workerKey}:${userKey}:${sessionKey}:${snapshot.paidEntitlement}`;
}

/**
 * Sets auth modal error text.
 */
export function setAuthError(host: TryUIController, message: string): void {
  if (!host.authError) return;
  host.authError.textContent = message;
  host.authError.classList.add('visible');
}

/**
 * Clears auth modal error text.
 */
export function clearAuthError(host: TryUIController): void {
  if (!host.authError) return;
  host.authError.textContent = '';
  host.authError.classList.remove('visible');
}

/**
 * Toggles auth modal loading state.
 */
export function setAuthLoading(host: TryUIController, loading: boolean): void {
  if (host.authSubmit) {
    host.authSubmit.disabled = loading;
  }
  if (host.authGoogle) {
    host.authGoogle.disabled = loading;
  }
}
